/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type {
  AdminDashUser,
  AdminDashUserResponse,
  CommunityType,
} from "@packages/sdk";
import { AccountType, RoleID, useActiveSidebarTab } from "@packages/sdk";
import type { ColumnConfig, TableProps } from "@packages/ui";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Avatar,
  Button,
  Checkbox,
  cssMediaQueryMinWidthTablet,
  IconButton,
  InfoIcon,
  lineHeights,
  Modal,
  ModalHeader,
  ScrollArea,
  Skeleton,
  SortIcon,
  spacing,
  Table,
  Text,
  useSearchParamState,
} from "@packages/ui";
import { Close as RadixClose } from "@radix-ui/react-dialog";
import { useParams } from "next/navigation";
import { Fragment, useEffect, useMemo } from "react";

import { getBasePADUrl, padTabsPaths } from "../../../../lib";
import {
  communityShowsTimeAttending,
  formatMemberJoinedDate,
  getMembershipLengthLocalizeKey,
} from "../../../../lib/utils/hallowAdminDashboard/members";
import { SubscriptionText } from "../../SubscriptionText";
import { EditMemberDropdownMenu } from "../EditMemberDropdownMenu";
import { MemberModalView } from "../MemberModalView";

const checkboxStyles: Interpolation = {
  height: 18,
  marginRight: spacing.s,
  width: 18,
};

const sortableHeaderStyles: Interpolation = {
  alignItems: "center",
  cursor: "pointer",
  display: "flex",
  gap: spacing.xs,
  userSelect: "none",
};

const skeletonStyles: Interpolation = {
  height: lineHeights.bodyS,
};

const tableStyles: Interpolation = {
  marginBottom: spacing.xxxl,
};

const scrollAreaStyles: Interpolation = {
  width: spacing.full,
};

const accountTypeHeader: Interpolation = {
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: spacing.xs,
};
const accountInfoModal: Interpolation = {
  maxWidth: 400,
  padding: spacing.xl,
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
  justifyContent: "flex-start",
  [cssMediaQueryMinWidthTablet]: {
    padding: spacing.xl,
  },
};
const accountInfoHeader: Interpolation = {
  justifyContent: "flex-start",
  margin: spacing.none,
  padding: spacing.none,
  width: spacing.full,
  "& h2": {
    marginBottom: spacing.negativeM,
    justifyContent: "flex-start",
    textAlign: "left",
    width: spacing.full,
  },
};

export type MemberWithAdminFlag = AdminDashUserResponse & {
  is_only_admin?: boolean;
};

/**
 * Columns the members table can sort by. All of them are sorted by the
 * backend, so every id here has to be a `sort_by` value the endpoint accepts.
 */
type SortableColumnId =
  | "email"
  | "first_name"
  | "joined_at"
  | "last_name"
  | "membership_length";

type SortState = {
  id: SortableColumnId;
  desc: boolean;
} | null;

type ParishAdminDashboardMembersProps = Partial<TableProps> & {
  communityId: number;
  communityType: CommunityType;
  loading?: boolean;
  loadingSkeletonsTotal?: number;
  members: MemberWithAdminFlag[];
  baseMemberLinkUrl: string;
  /**
   * The community's own timezone, so a member's joined date reads the same for
   * every admin looking at it rather than following each admin's browser.
   * Falls back to UTC when the community has none set.
   */
  communityTimeZone?: string | null;
  onSortChange?: (columnId: SortableColumnId) => void;
  sorting?: SortState;
  select?: {
    allIds: number[];
    selectedIds: number[];
    setSelectedIds: (ids: number[]) => void;
  };
  tableCSS?: Interpolation;
};

export const ParishAdminDashboardMembers = ({
  communityId,
  communityType,
  loading = false,
  loadingSkeletonsTotal = 3,
  members,
  baseMemberLinkUrl,
  communityTimeZone,
  onSortChange,
  sorting,
  select,
  tableCSS,
  ...props
}: ParishAdminDashboardMembersProps) => {
  const analytics = useAnalytics();
  const { communityId: padActiveCommunityId } = useParams<{
    communityId: string;
  }>();
  const [memberId, setMemberId] = useSearchParamState({
    defaultValue: "",
    key: "memberId",
  });
  const t = useTranslations();

  const activeSidebarTab = useActiveSidebarTab({
    baseUrl: getBasePADUrl({ communityId: parseInt(padActiveCommunityId) }),
    tabs: padTabsPaths,
  });

  const sortableLabel = (label: string, columnId: SortableColumnId) => {
    if (!onSortChange) return label;

    return (
      <div
        css={sortableHeaderStyles}
        onClick={() => onSortChange(columnId)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          // Space scrolls the page when it is pressed on anything that is not
          // a real button, and this header is a div for the table's styling.
          e.preventDefault();
          onSortChange(columnId);
        }}
        role="button"
        tabIndex={0}
      >
        {label}
        {sorting?.id === columnId ? (
          sorting.desc ? (
            <ArrowDownIcon size={18} style={{ cursor: "pointer" }} />
          ) : (
            <ArrowUpIcon size={18} style={{ cursor: "pointer" }} />
          )
        ) : (
          <SortIcon size={18} style={{ cursor: "pointer" }} />
        )}
      </div>
    );
  };

  const selectColumnConfig: ColumnConfig<MemberWithAdminFlag> = useMemo(
    () => ({
      label: (
        <Checkbox
          checked={
            select?.allIds.length > 0 &&
            select?.allIds.length === select?.selectedIds.length
          }
          name="selectAll"
          onCheckedChange={() =>
            select?.allIds.length === select?.selectedIds.length
              ? select?.setSelectedIds([])
              : select?.setSelectedIds(select?.allIds)
          }
          css={checkboxStyles}
        />
      ),
      render: (_, row) => (
        <Checkbox
          checked={select?.selectedIds.includes(row.id)}
          name="select"
          onCheckedChange={() =>
            select?.selectedIds.includes(row.id)
              ? select?.setSelectedIds(
                  select?.selectedIds.filter((id) => id !== row.id),
                )
              : select?.setSelectedIds([...select?.selectedIds, row.id])
          }
          onClick={(e) => e.stopPropagation()}
          css={checkboxStyles}
        />
      ),
      width: 0,
      order: 0,
    }),
    [select],
  );
  const imageColumnConfig: ColumnConfig<MemberWithAdminFlag> = {
    label: t("profile_tab_title_default"),
    order: 1,
    render: (val: string) => <Avatar imgSrc={val} size={48} />,
    width: 0,
  };
  const lastNameColumnConfig: Pick<
    ColumnConfig<MemberWithAdminFlag>,
    "label" | "order"
  > = {
    label: sortableLabel(t("admin_dash_last_name"), "last_name"),
    order: 2,
  };
  const nameColumnConfig: Pick<
    ColumnConfig<MemberWithAdminFlag>,
    "label" | "order"
  > = {
    label: sortableLabel(t("admin_dash_first_name"), "first_name"),
    order: 3,
  };
  const accountTypeColumnConfig: Pick<
    ColumnConfig<MemberWithAdminFlag>,
    "label" | "render"
  > = {
    label: (
      <div css={accountTypeHeader}>
        {t("web_admin_dash_account_type")}
        <Modal
          trigger={
            <IconButton
              icon={<InfoIcon size={16} />}
              variant="transparentNeutral"
              size="s"
            />
          }
          contentCSS={accountInfoModal}
        >
          <ModalHeader css={accountInfoHeader}>
            {t("web_admin_dash_account_types_info_header")}
          </ModalHeader>
          <Text>{t("web_admin_dash_account_types_info_text")}</Text>
          <RadixClose asChild>
            <Button>{t("web_admin_dash_account_types_info_cta")}</Button>
          </RadixClose>
        </Modal>
      </div>
    ),
    render: (val) =>
      val === AccountType.Kid
        ? t("web_admin_dash_account_type_kids")
        : t("web_admin_dash_account_type_standard"),
  };
  const moreMenuColumnConfig: Pick<
    ColumnConfig<MemberWithAdminFlag>,
    "label" | "render" | "width"
  > = useMemo(
    () => ({
      label: "",
      render: (_, row) => (
        <EditMemberDropdownMenu
          communityId={communityId}
          user={row}
          communityType={communityType}
        />
      ),
      width: 0,
    }),
    [communityId, communityType],
  );

  const columnConfig: {
    [key in keyof (AdminDashUser & {
      is_only_admin?: boolean;
    })]?: ColumnConfig<MemberWithAdminFlag>;
  } = useMemo(() => {
    if (communityType === "school") {
      return {
        select: selectColumnConfig,
        image_url: imageColumnConfig,
        last_name: {
          ...lastNameColumnConfig,
          width: "16%",
        },
        name: {
          ...nameColumnConfig,
          width: "16%",
        },
        login: {
          label: t("login_title"),
          order: 4,
          width: "16%",
        },
        account_type: {
          ...accountTypeColumnConfig,
          order: 5,
          width: "16%",
        },
        role_id: {
          label: t("admin_dash_role"),
          order: 6,
          width: "16%",
          render: (val: number) =>
            val === RoleID.Student
              ? t("admin_dash_role_student")
              : t("admin_dash_role_teacher"),
        },
        status: {
          label: t("general_word_subscription"),
          order: 7,
          render: (_, row) => (
            <SubscriptionText
              subscription_end_date={row.subscription_end_date as string}
            />
          ),
          width: "16%",
        },
        more_menu: {
          ...moreMenuColumnConfig,
          order: 8,
        },
      };
    }
    if (communityType === "religious_ed_program") {
      return {
        select: selectColumnConfig,
        image_url: imageColumnConfig,
        last_name: {
          ...lastNameColumnConfig,
          width: "33%",
        },
        name: {
          ...nameColumnConfig,
          width: "33%",
        },
        account_type: {
          ...accountTypeColumnConfig,
          order: 4,
          width: "33%",
        },
        more_menu: {
          ...moreMenuColumnConfig,
          order: 5,
        },
      };
    }
    // Time Attending is the parish icebreaker answer, so only a parish has
    // anything to put in it. Keyed on the community's own type rather than on
    // which page is rendering, so a child community that is itself a parish
    // gets the column and a small group under a parish does not.
    const showTimeAttending = communityShowsTimeAttending(communityType);
    // Five text columns on a parish, four everywhere else.
    const columnWidth = showTimeAttending ? "20%" : "25%";
    // Bios run long and wrap over several lines, so Bio takes 20% more than an
    // even share and the date columns give that back in equal parts. The
    // declared total is unchanged at 100%: 20 + 20 + 24 + 18 + 18 on a parish,
    // 25 + 25 + 30 + 20 everywhere else.
    const bioWidth = showTimeAttending ? "24%" : "30%";
    const dateColumnWidth = showTimeAttending ? "18%" : "20%";

    return {
      image_url: {
        ...imageColumnConfig,
        order: 0,
      },
      name: { ...nameColumnConfig, order: 1, width: columnWidth },
      last_name: { ...lastNameColumnConfig, order: 2, width: columnWidth },
      bio: { label: t("profile_bio"), order: 3, width: bioWidth },
      joined_at: {
        label: sortableLabel(t("admin_dash_joined_date"), "joined_at"),
        order: 4,
        render: (val) =>
          formatMemberJoinedDate({
            joinedAt: val as string | null,
            timeZone: communityTimeZone,
          }),
        width: dateColumnWidth,
      },
      ...(showTimeAttending && {
        membership_length: {
          label: sortableLabel(
            t("admin_dash_time_attending"),
            "membership_length",
          ),
          order: 5,
          // A member who never answered the icebreaker - most of them, it is
          // optional and predates a lot of memberships - renders blank rather
          // than an em-dash or a "not provided" label, and so does a value
          // this build does not know about.
          render: (val) => {
            const key = getMembershipLengthLocalizeKey(val as string | null);
            return key ? t(key) : "";
          },
          width: dateColumnWidth,
        },
      }),
      more_menu: {
        ...moreMenuColumnConfig,
        order: 6,
      },
    };
  }, [
    communityTimeZone,
    communityType,
    moreMenuColumnConfig,
    onSortChange,
    selectColumnConfig,
    sorting,
    t,
  ]);

  const handleRowClick = (row: Partial<MemberWithAdminFlag>) => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedCommunityMemberProfile,
      properties: {
        location: activeSidebarTab ? `${activeSidebarTab}_tab` : "",
      },
    });
    setMemberId(row.id.toString());
  };

  const skeleton = (column: keyof typeof columnConfig) => {
    if (column === "image_url") {
      return <Avatar size={48} loading />;
    }
    return <Skeleton css={skeletonStyles} />;
  };

  useEffect(() => {
    if (memberId)
      analytics.track({
        event: HallowAnalyticsEvent.TappedEditMember,
        properties: {
          member_id: memberId,
          screen_name: "members",
        },
      });
  }, [memberId]);

  const isOnlyAdmin = useMemo(() => {
    return members.find((m) => m.id === parseInt(memberId))?.is_only_admin;
  }, [members, memberId]);

  return (
    <Fragment>
      {memberId && (
        <MemberModalView
          communityId={communityId}
          memberId={parseInt(memberId)}
          isOnlyAdmin={isOnlyAdmin}
          // we can't use null because it's a searchParam state, so it needs to be a string
          onClose={() => setMemberId("")}
        />
      )}
      <ScrollArea
        horizontal
        viewportCSS={[scrollAreaStyles]}
        masksSize="xs"
        masksColor="onOverlayBackground"
      >
        <Table
          columns={columnConfig}
          data={members}
          skeletonRows={loadingSkeletonsTotal}
          onRowClick={handleRowClick}
          loadingSkeleton={skeleton}
          css={[tableStyles, tableCSS]}
          {...props}
        />
      </ScrollArea>
    </Fragment>
  );
};
